import React, { useState, useEffect, useCallback } from 'react';
import { IndianRupee, Pill, TestTube, Stethoscope, CreditCard } from 'lucide-react';
import Table from '../components/Table';
import FilterBar from '../components/FilterBar';
import { StatusBadge } from '../components/StatusBadge';
import { paymentService } from '../services/api';
import { formatCurrency, formatDate } from '../utils/helpers';

const FILTER_INITIAL = { search: '', type: '', method: '' };

const FILTER_FIELDS = [
  {
    key: 'type', label: 'Type', type: 'select',
    options: [
      { value: 'medicine',    label: 'Medicine'    },
      { value: 'lab_test',    label: 'Lab Test'    },
      { value: 'appointment', label: 'Appointment' },
    ],
  },
  {
    key: 'method', label: 'Payment Method', type: 'select',
    options: [
      { value: 'cod',    label: 'COD'    },
      { value: 'online', label: 'Online' },
      { value: 'wallet', label: 'Wallet' },
    ],
  },
];

const TYPE_LABEL = { medicine: 'Medicine', lab_test: 'Lab Test', appointment: 'Appointment' };

const getDescription = (p) => {
  if (p.type === 'medicine')    return `${p.uid || ''} · ${p.user_name || ''} · ${p.total_items || 0} items`;
  if (p.type === 'lab_test')    return `${p.lab_test_name || ''} · ${p.user_name || ''}`;
  if (p.type === 'appointment') return `${p.doctor_name || ''} · ${p.specialization || ''} · ${p.user_name || ''}`;
  return p.user_name || '';
};

const Payments = () => {
  const [payments, setPayments]     = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading]       = useState(false);
  const [page, setPage]             = useState(1);
  const [filters, setFilters]       = useState(FILTER_INITIAL);

  const fetchData = useCallback(async (pg = 1, f = filters) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      if (f.type)   params.type   = f.type;
      const res = await paymentService.getAll(params);
      console.log("payments res:", res)
      setPayments(res.payments);
      setPagination(res.pagination);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(1, filters); }, []);

  const onFilterChange = (key, val) => {
    const next = { ...filters, [key]: val };
    setFilters(next);
    setPage(1);
    fetchData(1, next);
  };

  const onReset = () => {
    setFilters(FILTER_INITIAL);
    setPage(1);
    fetchData(1, FILTER_INITIAL);
  };

  const activeCount = Object.values(filters).filter(Boolean).length;

  /* client-side search filter */
  const filtered = filters.search
    ? payments.filter(p => {
        const q = filters.search.toLowerCase();
        return [p.uid, p.user_name, p.user_mobile, p.payment_method, p.type].join(' ').toLowerCase().includes(q);
      })
    : payments;

  const stats = {
    total:       payments.reduce((s, p) => s + parseFloat(p.total_amount || 0), 0),
    medicine:    payments.filter(p => p.type === 'medicine').length,
    lab_test:    payments.filter(p => p.type === 'lab_test').length,
    appointment: payments.filter(p => p.type === 'appointment').length,
    online:      payments.filter(p => p.payment_method === 'online').length,
  };

  const columns = [
    {
      key: 'uid', label: 'ID',
      render: v => <span className="font-mono text-medihub-primary dark:text-medihub-highlight font-medium text-xs">{v || '—'}</span>,
    },
    {
      key: 'type', label: 'Type',
      render: v => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
          v === 'medicine' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
          v === 'lab_test' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
        }`}>{TYPE_LABEL[v] || v}</span>
      ),
    },
    {
      key: 'user_name', label: 'Customer',
      render: (v, row) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white text-sm">{v}</p>
          <p className="text-xs text-gray-400">{row.user_mobile}</p>
        </div>
      ),
    },
    {
      key: 'total_amount', label: 'Amount',
      render: v => <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(parseFloat(v || 0))}</span>,
    },

    {
      key: 'payment_method', label: 'Method',
      render: v => (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 uppercase">
          {v}
        </span>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: v => <StatusBadge status={v} />,
    },
    {
      key: 'created_at', label: 'Date',
      render: v => <span className="text-sm text-gray-500">{formatDate(v)}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Payments</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">All transactions — medicines, lab tests & appointments</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2.5 bg-green-50 dark:bg-green-900/20 rounded-xl shrink-0">
            <IndianRupee className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Amount</p>
            <p className="font-bold text-gray-900 dark:text-white">{formatCurrency(stats.total)}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-xl shrink-0">
            <Pill className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Medicine Orders</p>
            <p className="font-bold text-gray-900 dark:text-white">{stats.medicine}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2.5 bg-purple-50 dark:bg-purple-900/20 rounded-xl shrink-0">
            <TestTube className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Lab Tests</p>
            <p className="font-bold text-gray-900 dark:text-white">{stats.lab_test}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2.5 bg-teal-50 dark:bg-teal-900/20 rounded-xl shrink-0">
            <Stethoscope className="w-5 h-5 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Appointments</p>
            <p className="font-bold text-gray-900 dark:text-white">{stats.appointment}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="p-2.5 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl shrink-0">
            <CreditCard className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Online Payments</p>
            <p className="font-bold text-gray-900 dark:text-white">{stats.online}</p>
          </div>
        </div>
      </div>

      <FilterBar
        filters={filters}
        onChange={onFilterChange}
        onReset={onReset}
        searchPlaceholder="Search by ID, name, mobile…"
        fields={FILTER_FIELDS}
        activeCount={activeCount}
      />

      <Table
        title={`Transactions (${pagination.total ?? filtered.length})`}
        columns={columns}
        data={filtered}
        loading={loading}
        exportFilename="payments"
      />

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex justify-center gap-2 pt-2">
          <button
            disabled={page <= 1}
            onClick={() => { setPage(p => p - 1); fetchData(page - 1, filters); }}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40"
          >Prev</button>
          <span className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400">
            {page} / {pagination.total_pages}
          </span>
          <button
            disabled={page >= pagination.total_pages}
            onClick={() => { setPage(p => p + 1); fetchData(page + 1, filters); }}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40"
          >Next</button>
        </div>
      )}
    </div>
  );
};

export default Payments;
